import usePopulateAddressPropertyInfoFromEpc from '@/api/mutations/energyPerformanceCertificate/usePopulateAddressPropertyInfoFromEpc';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetSpruceProjectData from '@/api/queries/project/useGetSpruceProjectData';
import {
  Alert,
  Modal,
  UntitledLoading,
  useToastContext,
} from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { EpcPromptStep } from './EpcPromptStep';
import { PropertyInfoStep } from './PropertyInfoStep';

type PushToSpruceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
};

const PushToSpruceModal: FC<PushToSpruceModalProps> = (props) => {
  const { isOpen, onClose, projectId } = props;

  return (
    <Modal title="Push to Spruce" isOpen={isOpen} onClose={onClose}>
      {isOpen && (
        <PushToSpruceModalContent projectId={projectId} onClose={onClose} />
      )}
    </Modal>
  );
};

type Step = 'epc-prompt' | 'property-info';

type PushToSpruceModalContentProps = {
  projectId: string;
  onClose: () => void;
};

const PushToSpruceModalContent: FC<PushToSpruceModalContentProps> = (props) => {
  const { projectId, onClose } = props;
  const { pushSuccess, pushError } = useToastContext();
  const queryClient = useQueryClient();

  const { siteAddress, isPending } = useGetSpruceProjectData(projectId);
  const { mutate: populatePropertyInfo, isPending: isPopulating } =
    usePopulateAddressPropertyInfoFromEpc({
      projectId,
      options: {
        meta: { skipGlobalErrorToast: true },
        onError: () => {
          pushError(
            'Property information could not be auto-filled from the EPC'
          );
        },
      },
    });

  const hasEpc = (siteAddress?.energyPerformanceCertificates?.length ?? 0) > 0;
  const propertyInfo = siteAddress?.propertyInformation;

  const hasPropertyInfo = !!propertyInfo;

  // For legacy projects where an EPC was linked before auto-population
  // existed, trigger population of property info from the EPC now.
  useEffect(() => {
    if (!siteAddress || !hasEpc || hasPropertyInfo) return;
    populatePropertyInfo({ addressId: siteAddress.publicId });
  }, [siteAddress, hasEpc, hasPropertyInfo, populatePropertyInfo]);

  const [step, setStep] = useState<Step>(() =>
    hasEpc ? 'property-info' : 'epc-prompt'
  );

  // Re-evaluate step when data changes (e.g. after EPC sync)
  const effectiveStep =
    step === 'epc-prompt' && hasEpc ? 'property-info' : step;

  if (isPending || isPopulating) {
    return (
      <Modal.Body>
        <UntitledLoading className="mx-auto size-8" />
      </Modal.Body>
    );
  }

  if (!siteAddress) {
    return (
      <Modal.Body>
        <Alert colour="red">
          This project does not have a site address. Please add one before
          pushing to Spruce.
        </Alert>
      </Modal.Body>
    );
  }

  return effectiveStep === 'epc-prompt' ? (
    <EpcPromptStep
      addressId={siteAddress.publicId}
      projectId={projectId}
      onSkip={() => setStep('property-info')}
      onEpcLinked={() => {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.spruceProjectData(projectId),
        });
        setStep('property-info');
      }}
    />
  ) : (
    <PropertyInfoStep
      addressId={siteAddress.publicId}
      projectId={projectId}
      propertyInfo={propertyInfo ?? undefined}
      onClose={onClose}
      onSuccess={() => {
        pushSuccess('Project pushed to Spruce successfully');
        onClose();
      }}
      onError={(message: string) => {
        pushError(message);
      }}
    />
  );
};

export default PushToSpruceModal;
